import Amount from "@funds/components/atoms/amount";
import { calculateFutureValue, calculateMonthlySIP } from "creso-core/utils";
import { GoalIcon } from "lucide-react";
import { Input, V2Label } from "web-design-system";
import { imageAssetUrl } from "@/utils/url";
import { useCallback, useEffect } from "react";
import { useTheme } from "@/theme";
import { useProposalFormContext } from "../../providers/proposal-provider";
import { durationDisplayFormat, GoalIllustration } from "../../config";

export function BasicInfo() {
  const { watch, setValue, getValues } = useProposalFormContext();

  const goal = watch("goal");

  const includeLinkedHoldings = watch("include_linked_holdings");

  const updateMonthlySipValue = useCallback(
    (includeLinkedHoldings: boolean) => {
      if (!goal?.id) return;
      const goalFv = calculateFutureValue(
        Number(goal.target_corpus),
        Number(goal.expected_inflation),
        goal.duration_months
      );
      const expectedReturn = getValues("expected_return") || 12;
      const totalLumpsumAmount = getValues("total_lumpsum_amount") || 0;
      const duration = getValues("duration_months") || goal.duration_months;
      const linkedHoldingsFutureValue = includeLinkedHoldings
        ? calculateFutureValue(
            Number(goal.total_current_value ?? 0),
            Number(expectedReturn),
            duration
          )
        : 0;
      const effectiveTargetAmount = goalFv - linkedHoldingsFutureValue;
      const newSipAmount = Math.round(
        calculateMonthlySIP(
          effectiveTargetAmount,
          Number(expectedReturn),
          duration,
          Number(totalLumpsumAmount)
        )
      );
      setValue("total_sip_amount", newSipAmount.toString());
    },
    [getValues, setValue, goal]
  );

  useEffect(
    () => updateMonthlySipValue(includeLinkedHoldings),
    [updateMonthlySipValue, includeLinkedHoldings]
  );

  const { currentTheme } = useTheme();

  const illustration = GoalIllustration[currentTheme][goal?.category as string];

  return (
    <form className="flex flex-col space-y-6">
      {goal?.id ? (
        <div className="relative w-full border border-border overflow-hidden rounded-lg">
          <div className="h-60 overflow-hidden">
            <img
              src={illustration}
              className="w-full h-full object-cover"
              alt={goal.name}
            />
          </div>
          <div className="flex bg-background rounded-b-lg p-4 gap-6 top-[calc(100%-20px)] left-0 right-0 flex-col">
            <div className="flex items-center">
              <div className="flex items-center gap-3 text-sm">
                <GoalIcon className="size-4" />
                <span>
                  Target in {durationDisplayFormat(goal.duration_months / 12)}
                </span>
              </div>
              <span className="text-sm ml-auto font-semibold">
                <Amount precision={0}>
                  {calculateFutureValue(
                    Number(goal.target_corpus),
                    Number(goal.expected_inflation),
                    goal.duration_months
                  )}
                </Amount>
              </span>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-slate-200 h-70 relative w-full aspect-video rounded-lg">
            <img
              className="w-60 h-40 absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-[calc(50%-30px)]"
              src={imageAssetUrl("proposals/proposal-group.webp")}
            />
          </div>
        </>
      )}
      <div className="flex flex-col gap-1.5">
        <V2Label>Name</V2Label>
        <Input
          value={watch("name")}
          onChange={(e) => setValue("name", e.target.value)}
        />
      </div>
    </form>
  );
}
